import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { PsButtonTuner } from 'stencilSetup';
import { ChoosePCPDrawerForm } from './ChoosePCPDrawerForm';
import { SearchProvider } from './SearchProvider';
import { api } from 'theSource/utils';
import { useConfigurationQuery } from 'theSource/queries';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import {
  selectPCPProviderInfoLogic,
  setCheckedDependentCheckbox,
  setOpenChoosePCPDrawer,
  setSubscriberCheckbox,
} from './SearchResultsPCPDrawer/services';
import './ChoosePCPDrawer.css';
import { BenefitPlanDto, PcpMembersDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { FieldValueDto } from 'theSource/types';
import { AdditionalPlanElectionDataDto } from '../../types';
import { closeSideDrawer } from 'enrollment/utils/common/Utils';
import { PageContentQueryResponseDto } from 'enrollment/queries/usePageContentQuery/types';

export interface ChoosePCPDrawerProps {
  electPlan?: () => void;
  setPcpElectionData: (data: AdditionalPlanElectionDataDto) => void;
  setPcpDrawerRequired: (value: boolean) => void;
  fromPlanDetails?: boolean;
  selectedDependentIds?: number[];
  openChooseProviderDrawer: boolean;
  setShowPcpDrawer: (value: boolean) => void;
  setOpenChooseProviderDrawer: (value: boolean) => void;
  plan?: BenefitPlanDto;
  hideDependentCheckboxWhenChecked: { [key: string]: boolean };
  setHideDependentCheckboxWhenChecked: (value: { [key: string]: boolean }) => void;
  subscribeZipCode?: FieldValueDto;
  selected?: boolean;
  pcpMembers?: PcpMembersDto;
  isEsignPlanRequired?: boolean;
  pageContent: PageContentQueryResponseDto | undefined;
}

export const ChoosePCPDrawer = ({
  electPlan,
  setPcpElectionData,
  setPcpDrawerRequired,
  fromPlanDetails = false,
  selectedDependentIds,
  openChooseProviderDrawer,
  setShowPcpDrawer,
  setOpenChooseProviderDrawer,
  plan,
  hideDependentCheckboxWhenChecked,
  setHideDependentCheckboxWhenChecked,
  subscribeZipCode,
  selected = false,
  pcpMembers,
  isEsignPlanRequired,
  pageContent,
}: ChoosePCPDrawerProps) => {
    
  const [providerData, setProviderData] = useState([]);
  const [locationInput, setLocationInput] = useState(subscribeZipCode || '');
  const [milesValue, setMilesValue] = useState<number>(10);
  const [providerNameValue, setProviderNameValue] = useState<string>('');
  const [officeNameValue, setOfficeNameValue] = useState<string>('');
  const [specialityValue, setSpecialityValue] = useState<string[]>([]);
  const [inNetworkValue, setInNetworkValue] = useState<boolean>(false);
  const [acceptNewPatientValue, setAcceptNewPatientValue] =
    useState<boolean>(false);
  const [specialityValueOptions, setSpecialityValueOptions] = useState({});
  const [disabledSearchProviderBtn, setDisabledSearchProviderBtn] =
    useState<boolean>(locationInput ? false : true);

  const { t } = useTranslation('enrollment.ChoosePCPDrawer');

  const { configuration } = useConfigurationQuery();

  const lumosApiHost = configuration?.apiHost;

  const dispatch = useDispatch();

  const providerInfoLogicSelector = useSelector(selectPCPProviderInfoLogic);
  const checkDependentCheckbox =
    providerInfoLogicSelector?.isDependentCheckboxChecked;

  const openChoosePCPDrawer = providerInfoLogicSelector.isChoosePCPDrawerOpen;

  const handleFindProviderDrawer = () => {
    dispatch(setOpenChoosePCPDrawer(true));

    api
      .get(`${lumosApiHost}/v1/self_service/primary_care_providers/search`, {
        org_plan_id: plan?.plan_id,
        zip_code: subscribeZipCode,
        distance: 10,
      })
      .then((response: any) => {
        setSpecialityValueOptions(response?.filters);
      })
      .catch((err) => {
        console.log(err?.response?.data?.details);
      });
  };
  const handleLocationInput = (e: any) => {
    const value = e.target.value;
    setLocationInput(e.target.value);
    value ? setDisabledSearchProviderBtn(false) : setDisabledSearchProviderBtn(true);
  };

  const handleMilesInput = (e: any) => {
    setMilesValue(e.target.value);
  };

  const handleProviderNameInput = (e: any) => {
    setProviderNameValue(e.target.value);
  };

  const handleOfficeNameInput = (e: any) => {
    setOfficeNameValue(e.target.value);
  };

  const handleInNetworkChange = (e: any) => {
    setInNetworkValue(e.target.checked);
  };

  const handleAcceptNewPatient = (e: any) => {
    setAcceptNewPatientValue(e.target.checked);
  };

  const handleSpecialityChange = (e: any) => {
    if (e.target.classList.contains('is-checked')) {
      // Get the text content of the element
      const value = e.target.textContent || '';
      // Store the value in the array
      setSpecialityValue((prevValues: any) => [...prevValues, value.trim()]);
    } else {
      // Get the text content of the element
      const value = e.target.textContent || '';

      // Remove the value from the array if it exists
      setSpecialityValue((prevValues: any) => {
        const index = prevValues.indexOf(value.trim());
        if (index > -1) {
          // Copy the array to avoid direct state mutation
          const updatedValues = [...prevValues];
          // Remove the item
          updatedValues.splice(index, 1);
          return updatedValues;
        }
        return prevValues; // Return the original array if the value was not found
      });
    }
  };

  const handleCloseChoosePCPDrawer = () => {
    dispatch(setOpenChoosePCPDrawer(false));

    locationInput ? setDisabledSearchProviderBtn(false) : setDisabledSearchProviderBtn(true);

    Object?.keys(checkDependentCheckbox)?.forEach((id) => {
      dispatch(setCheckedDependentCheckbox({ id, checked: false }));
    });
    dispatch(setSubscriberCheckbox(false));

    setLocationInput(subscribeZipCode || '');
    setMilesValue(10);
    setProviderNameValue('');
    setOfficeNameValue('');
    setInNetworkValue(false);
    setAcceptNewPatientValue(false);
    closeSideDrawer();
  };

  return (
    <div className="text-center">
      {!selected && <PsButtonTuner
        buttonId="choose-add-providers-btn"
        data-testid="choose-add-providers-btn"
        level="primary"
        label={t('dental.buttonLabel')}
        icon="binoculars"
        extraClass="mt-5"
        onClick={handleFindProviderDrawer}
      />}
      <OrbitDrawer
        data-testid={`choose-add-providers-drawer-${plan?.plan_id}`}
        drawerId={`choose-add-providers-drawer-${plan?.plan_id}`}
        heading={t('dental.heading')}
        isOpen={openChoosePCPDrawer}
        handleClose={handleCloseChoosePCPDrawer}
        dialogLarge={true}
        closeIcon="back"
        saveButtonDisabled={true}
      >
        <div data-testid="choose-add-providers-drawer-content">
          <ChoosePCPDrawerForm
            specialityValueOptions={specialityValueOptions}
            handleLocationInput={handleLocationInput}
            locationInput={String(locationInput)}
            handleMilesInput={handleMilesInput}
            milesValue={milesValue}
            providerNameValue={providerNameValue}
            handleProviderNameInput={handleProviderNameInput}
            officeNameValue={officeNameValue}
            handleOfficeNameInput={handleOfficeNameInput}
            inNetworkValue={inNetworkValue}
            handleInNetworkChange={handleInNetworkChange}
            acceptNewPatientValue={acceptNewPatientValue}
            handleAcceptNewPatient={handleAcceptNewPatient}
            handleSpecialityChange={handleSpecialityChange}
          />
          <SearchProvider
            disabledSearchProviderBtn={disabledSearchProviderBtn}
            providerData={providerData}
            setProviderData={setProviderData}
            locationInput={String(locationInput)}
            milesValue={milesValue}
            providerNameValue={providerNameValue}
            officeNameValue={officeNameValue}
            inNetworkValue={inNetworkValue}
            acceptNewPatientValue={acceptNewPatientValue}
            specialityValue={specialityValue}
            electPlan={electPlan}
            setPcpElectionData={setPcpElectionData}
            setPcpDrawerRequired={setPcpDrawerRequired}
            fromPlanDetails={fromPlanDetails}
            selectedDependentIds={selectedDependentIds}
            openChooseProviderDrawer={openChooseProviderDrawer}
            setOpenChooseProviderDrawer={setOpenChooseProviderDrawer}
            setShowPcpDrawer={setShowPcpDrawer}
            setDisabledSearchProviderBtn={setDisabledSearchProviderBtn}
            plan={plan}
            hideDependentCheckboxWhenChecked={hideDependentCheckboxWhenChecked}
            setHideDependentCheckboxWhenChecked={setHideDependentCheckboxWhenChecked}
            selected={selected}
            pcpMembers={pcpMembers || {} as PcpMembersDto}
            isEsignPlanRequired={isEsignPlanRequired}
            pageContent={pageContent}
          />
        </div>
      </OrbitDrawer>
    </div>
  );
};
